// <copyright file="AwsConsole.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import { ProgressSpinner } from "primereact/progressspinner";
import React, { useState as _useState, useContext } from "react";
import styles from "src/components/AwsConsole/AwsConsole.module.css";
import ConfigContext from "src/context/ConfigContext";
import { getAwsFederatedSigninUrl, LoadingStatus } from "src/utils/SharedUtils";
import type { Toast } from "primereact/toast";
import type { FC } from "react";
import type { IConfig } from "src/utils/ConfigLoader";

interface AwsConsoleProps {
    useState?: typeof _useState; // optional hook override (for tests)
}

// Info/error message popup
const toast = React.createRef<Toast>();

// Main content focus item
const mainContentRef = React.createRef<HTMLAnchorElement>();

/**
 * Determines whether to display error message
 * @param errorMessage Value of error message
 * @param loadingStatus Status of loading sign-in URL for AWS console
 * @returns Indicator whether error message should be displayed
 */
function shouldDisplayError(
    errorMessage: string,
    loadingStatus: LoadingStatus
) {
    return errorMessage !== "" && loadingStatus === LoadingStatus.error;
}

/**
 * Component to render an AWS console signin url with temporary credentials
 * @returns The AWS console signin component
 */
const AwsConsole: FC<AwsConsoleProps> = ({ useState = _useState }) => {
    const configData = useContext(ConfigContext) as IConfig;
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.preload);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const doLinkAction = (
        event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>,
        configData: IConfig,
        linkRef: string
    ) => {
        event.preventDefault();

        if (event.type === 'click' || (event.type === 'keydown' && (event as React.KeyboardEvent).key === 'Enter')) {
            const fetchLink = async () => {
                const destination = linkRef;
                console.debug(`encoded destination url: ${encodeURI(destination)}`);
                await getAwsFederatedSigninUrl(
                    `${configData.apiBaseUrl}`,
                    destination,
                    toast.current,
                    setLoadingStatus
                )
                    .then((url) => {
                        console.debug(`Setting url: ${url}`);
                        console.debug(`Loading status: ${loadingStatus}`);
                        setErrorMessage("");
                        window.open(url, "_blank");
                    })
                    .catch((error) => {
                        // Loading status and toast message for errors done in util function
                        const message = `${error.summary}: ${error.detail}`;
                        console.debug(`Setting error: ${message}`);
                        console.debug(`Loading status: ${loadingStatus}`);
                        setErrorMessage(message);
                    });
            };
            fetchLink();
        }
    };

    // Main component display
    /* eslint-disable react/jsx-no-target-blank */
    return (
        <div className={styles.AwsConsole} data-testid="AwsConsole">
            {shouldDisplayError(errorMessage, loadingStatus) && (
                <div className="App-content-error">{errorMessage}</div>
            )}
            <div className={styles.AwsLinks}>
                <div className={styles.LinkItem}>
                    <div>
                        <a
                            ref={mainContentRef}
                            href="https://us-east-1.console.aws.amazon.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            tabIndex={0}
                            onClick={(event) =>
                                doLinkAction(
                                    event,
                                    configData,
                                    "https://us-east-1.console.aws.amazon.com"
                                )
                            }
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    doLinkAction(
                                        event,
                                        configData,
                                        "https://us-east-1.console.aws.amazon.com"
                                    );
                                }
                            }}
                        >
                            {"Aws Console"}
                        </a>{" "}
                        (opens a new tab/page)
                    </div>
                    {loadingStatus === LoadingStatus.loading && (
                        <div className="AwsLinkProgress">
                            <ProgressSpinner
                                style={{
                                    height: "20px",
                                    width: "20px",
                                    transform: "scale(1)",
                                }}
                                aria-label="Loading"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
    /* eslint-enable */
};

export default AwsConsole;
